import styled, { css } from 'styled-components';

import { Typography } from '@speeki/global-ui-components';

/**
 * Hosts an email field with the confirmation slot attached to it. The slot
 * measures itself and writes `--attached-slot-width` here so the typed
 * address never runs underneath it; focus re-pads the library input, so both
 * paddings are covered.
 */
export const AttachedField = styled.div`
    position: relative;
    width: 100%;

    /* Only the email field's own input - the first child is the TextField;
     * the code boxes render in later siblings and must keep the library's
     * box size. */
    > *:first-child input {
        padding-right: calc(var(--attached-slot-width, 0px) + 16px);
    }

    > *:first-child input:focus {
        padding-right: calc(var(--attached-slot-width, 0px) + 15px);
    }
`;

/*
 * The slot sits inside the input box, flush with its right edge. Its top and
 * height come from the measured input box (labels wrap in other languages and
 * shift the input down); the fallbacks match the library's single-line-label
 * geometry - a 20px label row plus its 8px gap above a 52px input, inset for
 * the 2px focus border.
 */
const slotGeometry = css`
    align-items: center;
    display: flex;
    gap: 6px;
    height: var(--attached-slot-height, 48px);
    padding: 0 14px;
    position: absolute;
    right: 2px;
    top: var(--attached-slot-top, 30px);
    white-space: nowrap;
`;

export const SlotButton = styled.button(
    ({ theme: { palette } }) => css`
        ${slotGeometry};
        background: ${palette.neutral.lightGrey};
        border: none;
        border-left: 1px solid ${palette.neutral.dividers};
        color: ${palette.tertiary.default};
        cursor: pointer;
        font: inherit;
        font-size: 14px;

        &:disabled {
            color: ${palette.text.assistive};
            cursor: default;
        }
    `,
);

export const SlotBadge = styled.div(
    ({ theme: { palette } }) => css`
        ${slotGeometry};
        background: ${palette.neutral.lightGrey};
        border-left: 1px solid ${palette.semantic.success};
        color: ${palette.semantic.success};
        font-size: 14px;
    `,
);

export const ConfirmationWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    width: 100%;
`;

export const ConfirmationHint = styled(Typography.TextBody_14_20)(
    ({ theme: { palette } }) => css`
        color: ${palette.text.assistive};
        margin-top: 12px;
    `,
);

export const CodeFieldWrapper = styled.div`
    margin-top: 4px;
`;

/* The code boxes and the resend affordance share a row, per the wireframe -
 * the resend wraps under the boxes only when the column is too narrow. */
export const CodeRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
`;

export const ResendHint = styled(Typography.TextBody_14_20)(
    ({ theme: { palette } }) => css`
        color: ${palette.text.assistive};
    `,
);

export const CodeLabel = styled(Typography.TextBody_14_20)(
    ({ theme: { palette } }) => css`
        color: ${palette.text.primary};
        display: block;
        margin-bottom: 4px;
    `,
);

export const InlineAction = styled.button(
    ({ theme: { palette } }) => css`
        background: none;
        border: none;
        color: ${palette.tertiary.default};
        cursor: pointer;
        font: inherit;
        padding: 0;
        text-decoration: underline;

        &:disabled {
            color: ${palette.text.assistive};
            cursor: default;
            text-decoration: none;
        }
    `,
);
